import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { TPromoBannerBase } from '@onesource/schemas';
import { isRichHtmlEmpty, sanitizeRichHtml, stripHtml } from '@onesource/utils';
import { FC } from 'react';
import { MdChevronRight } from 'react-icons/md';

import { useGetByBreakpoint } from '../../hooks';
import mobileImg from '../banner-with-lauren/mobile-img.png';
import { resolvePromoBannerBackground } from '../promo-banner-colors';
import { promoBannerRichTextSx } from '../promo-banner-rich-text';
import { SidebarPromoBannerWithLaurenImageMobile } from './mobile-component';

/**
 * Width at which the sidebar banner switches from the full-width mobile bottom
 * bar to the fixed-width vertical card. Page slots that size/pin the SIDEBAR
 * banner (see lesson-detail-view) must switch their sx at this same width —
 * a mismatch crams the full-width bar into the 240px card box.
 */
export const SIDEBAR_BANNER_DESKTOP_MIN_WIDTH = 1400;

export interface SidebarPromoBannerWithLaurenImageProps {
  banner: TPromoBannerBase;
}

/**
 * Vertical promo banner layout sized for the right-side page rail
 * (PromoBannerPosition.SIDEBAR). A narrow gradient card stacking the headline,
 * the review/description pill, the seated Lauren pose and the CTA. Below
 * SIDEBAR_BANNER_DESKTOP_MIN_WIDTH the slot pins the banner to the bottom of
 * the viewport, so it switches to its own compact floating-bar layout
 * (SidebarPromoBannerWithLaurenImageMobile).
 */
export const SidebarPromoBannerWithLaurenImage: FC<SidebarPromoBannerWithLaurenImageProps> = ({ banner }) => {
  const isMobileLayout = useGetByBreakpoint('down', SIDEBAR_BANNER_DESKTOP_MIN_WIDTH);

  // Below the desktop cutoff render the dedicated mobile bar — it handles the
  // mobile copy/visibility overrides itself.
  if (isMobileLayout) {
    return <SidebarPromoBannerWithLaurenImageMobile banner={banner} />;
  }

  const isExternal = /^https?:\/\//i.test(banner.buttonUrl);
  const { title, description } = banner;
  // Admins can hide the review pill without deleting the copy.
  // `!== false` keeps banners visible while an older API omits the flag.
  const isDescriptionVisible = banner.isDescriptionActive !== false;
  // Title is rich HTML; alt must be plain text.
  const imageAlt = stripHtml(title);

  return (
    <Stack
      spacing={{ xs: 1, md: 2 }}
      sx={{
        width: '100%',
        maxWidth: 240,
        // Fill the slot when it sets a height (lesson-detail-view sizes the
        // rail to the hero player, offset below the video title row); resolves
        // to auto height otherwise. No mt here — vertical placement belongs to
        // the page's slot, not the card.
        height: '100%',
        justifyContent: 'space-between',
        p: 1.8,
        borderRadius: '16px',
        overflow: 'hidden',
        background: resolvePromoBannerBackground(banner.backgroundColor),
      }}
    >
      <Box
        sx={{
          ...promoBannerRichTextSx,
          color: theme => theme.palette.common.white,
          fontWeight: 700,
          fontSize: 13,
          lineHeight: '19px',
          textAlign: 'center',
          '& span': { fontSize: 13 },
        }}
        dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(title) }}
      />

      {/* The image row absorbs the flexible space when the card is stretched
          to the slot height, so the image shrinks/centers instead of the copy
          and CTA drifting apart unevenly. */}
      <Box sx={{ flex: '1 1 auto', minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box
          component="img"
          src={mobileImg}
          alt={imageAlt}
          sx={{
            width: 'auto',
            maxWidth: 140,
            height: '100%',
            maxHeight: 200,
            objectFit: 'contain',
            objectPosition: 'bottom',
          }}
        />
      </Box>

      {isDescriptionVisible && !isRichHtmlEmpty(description) && (
        <Box
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '4px',
            px: 0.6,
            py: 0.5,
          }}
        >
          <Box
            sx={{
              ...promoBannerRichTextSx,
              fontStyle: 'italic',
              color: '#000',
              fontSize: '9px',
              lineHeight: 1.2,
              textAlign: 'center',
            }}
            dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(description) }}
          />
        </Box>
      )}

      <Button
        variant="outlined"
        fullWidth
        href={banner.buttonUrl}
        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        endIcon={<MdChevronRight size={20} />}
        sx={{
          minHeight: '34px',
          borderRadius: '8px',
          color: '#fff !important',
          borderColor: theme => theme.palette.common.white,
          fontSize: '12px',
          fontWeight: 700,
          px: 1.5,
          display: 'flex',
          justifyContent: 'center',
          whiteSpace: 'nowrap',
          '& .MuiButton-endIcon': { ml: 0.25 },
          '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255, 255, 255, 0.14)' },
        }}
      >
        {banner.buttonLabel}
      </Button>
    </Stack>
  );
};
